import { TextField } from "@mui/material";
import { FormikErrors, FormikTouched } from "formik";
import { makeSxStyles } from "ui";
import FieldRow from "@/shared/components/FieldRow";
import { US_STATES } from "@/shared/constants/us-states";

export interface AddressDerivedFieldValues {
  state?: string;
  city?: string | null;
  zipCode?: string | null;
}

interface AddressDerivedFieldsProps {
  values: AddressDerivedFieldValues;
  errors: FormikErrors<AddressDerivedFieldValues>;
  touched: FormikTouched<AddressDerivedFieldValues>;
}

// State/City/ZIP for the organization forms. Shared by Add and Edit - both
// are only ever set via the Address autocomplete selection (ESR-1632/
// ESR-1633), so they're rendered as plain read-only values rather than as
// editable-looking inputs (no borders, no State dropdown affordance).
const AddressDerivedFields = ({
  values,
  errors,
  touched,
}: AddressDerivedFieldsProps) => {
  const styles = useStyles();

  return (
    <>
      <FieldRow label="State">
        <TextField
          variant="standard"
          value={
            US_STATES.find((state) => state.code === values.state)?.name || "-"
          }
          disabled
          InputProps={{ readOnly: true, disableUnderline: true }}
          sx={styles.readOnlyField}
          error={touched.state && !!errors.state}
          helperText={touched.state && errors.state}
          fullWidth
        />
      </FieldRow>
      <FieldRow label="City">
        <TextField
          variant="standard"
          value={values.city || "-"}
          disabled
          InputProps={{ readOnly: true, disableUnderline: true }}
          sx={styles.readOnlyField}
          error={touched.city && !!errors.city}
          helperText={touched.city && errors.city}
          fullWidth
        />
      </FieldRow>
      <FieldRow label="ZIP Code">
        <TextField
          variant="standard"
          value={values.zipCode || "-"}
          disabled
          InputProps={{ readOnly: true, disableUnderline: true }}
          sx={styles.readOnlyField}
          error={touched.zipCode && !!errors.zipCode}
          helperText={touched.zipCode && errors.zipCode}
          fullWidth
        />
      </FieldRow>
    </>
  );
};

const useStyles = makeSxStyles(() => ({
  // Plain-value look: keep the normal text color instead of MUI's default
  // faded "disabled" treatment, since this isn't an unavailable control -
  // it's a read-only display of what the Address selection derived.
  readOnlyField: {
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: "currentColor",
      color: "text.primary",
    },
  },
}));

export default AddressDerivedFields;
